<template>
	<div class="daily-quests lok-scrollbar">
		<MainDailyQuestBox
			v-if="mainDailyQuest"
			:quest="mainDailyQuest"
			:key="mainDailyQuest.id"
			:finished="isMainDailyQuestFinished"
			@claim-rewards="claimDailyQuest(mainDailyQuest.id)"
		/>
		<DailyQuestBox
			v-for="(quest, i) in dailyQuests"
			:quest="quest"
			:key="i"
			@claim-rewards="claimDailyQuest(quest.id)"
		/>
		<DailyQuestBox
			v-for="(quest, i) in finishedDailyQuests"
			finished
			:quest="quest"
			:key="i"
			@claim-rewards="claimDailyQuest(quest.id)"
		/>
	</div>
</template>

<script lang="ts" setup>
import DailyQuestBox from "@/components/Quests/DailyQuestBox.vue";
import MainDailyQuestBox from "@/components/Quests/MainDailyQuestBox.vue";
import { QuestObjectiveActions } from "@/game/entities/config/quests/quest_objective_actions";
import { QuestTypes } from "@/game/entities/config/quests/quest_types.js";
import { MAIN_DAILY_QUEST_ID } from "@/game/entities/config/quests/quests";
import QuestRepository from "@/game/QuestRepository";
import useQuestStore from "@/store/useQuestStore";
import type { QuestActive } from "@/types/quest";
import { storeToRefs } from "pinia";
import { computed } from "vue";

const questStore = useQuestStore();
const { getActiveQuests } = storeToRefs(questStore);
const emit = defineEmits(["claimRewards"]);

const dailyQuests = computed(
	(): Array<QuestActive> | null =>
		getActiveQuests.value.filter(
			(q: QuestActive): boolean =>
				q.type === QuestTypes.DAILY && q.id !== MAIN_DAILY_QUEST_ID,
		) || null,
);

const finishedDailyQuests = computed(
	// filter out id 9001
	(): Array<QuestActive> | null =>
		questStore
			.getFinishedQuests()
			.filter(
				(q: QuestActive): boolean =>
					q.type === QuestTypes.DAILY && q.id !== MAIN_DAILY_QUEST_ID,
			) || null,
);

const mainDailyQuest = computed(
	(): QuestActive | null =>
		getActiveQuests.value.find(
			(q: QuestActive): boolean =>
				q.type === QuestTypes.DAILY && q.id === MAIN_DAILY_QUEST_ID,
		) ||
		questStore
			.getFinishedQuests()
			.find(
				(q: QuestActive): boolean =>
					q.type === QuestTypes.DAILY && q.id === MAIN_DAILY_QUEST_ID,
			) ||
		null,
);

const isMainDailyQuestFinished = computed((): boolean => {
	if (!mainDailyQuest.value) {
		return false;
	}
	return questStore
		.getFinishedQuests()
		.some((q: QuestActive): boolean => q.id === mainDailyQuest.value?.id);
});

const claimDailyQuest = (id: number): void => {
	QuestRepository.getInstance().manageQuestState(
		QuestObjectiveActions.COMPLETE_DAILY_QUESTS,
		null,
		1,
	);
	emit("claimRewards", id);
};
</script>

<style lang="scss" scoped>
.daily-quests {
	width: 100%;
	@include flex-col-start;
	position: relative;
	padding: 1rem;

	.no-quest-texts {
		@include flex-col-center;
	}
	.pin-message {
		position: absolute;
		top: 0;
		right: 0.6rem;
	}

	div {
		&:last-child {
			margin-bottom: 0.5rem;
		}
	}
}
</style>
